import { useEffect, useRef } from "react";
import PawSurprise from "../components/PawSurprise";
import OffersSection from "../components/OffersSection";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Star, ShieldCheck, Sparkles, HomeIcon, Heart,
  Award, Quote, CheckCircle2, Phone, MapPin,
} from "lucide-react";
import { SERVICES, TESTIMONIALS, TRUST_POINTS, BRAND, GALLERY } from "../data/mock";
import LatestBlogSection from "../components/LatestBlogSection";
import LocalBusinessSchema from "../components/LocalBusinessSchema";

const u = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=240&h=240&q=80`;
const HERO_PETS = [
  { src: u("photo-1740011962598-73f722075dc1"), alt: "Groomed Shih Tzu", pos: "top-[18%] -left-2 sm:left-2", size: "w-14 h-14 sm:w-20 sm:h-20", delay: "0.4s" },
  { src: u("photo-1652342516868-50eac8c9af0d"), alt: "Groomed poodle", pos: "bottom-[24%] -right-2 sm:right-0", size: "w-16 h-16 sm:w-24 sm:h-24", delay: "1.6s" },
  { src: u("photo-1536590158209-e9d615d525e4"), alt: "Fluffy Persian cat", pos: "top-[2%] left-[38%] hidden sm:block", size: "w-16 h-16", delay: "2.4s" },
];


export default function Home() {
  const featured = SERVICES.slice(0, 6);
  const FirstIcon = featured[0].icon;
  const heroBgRef = useRef(null);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 1200);
        if (heroBgRef.current) heroBgRef.current.style.transform = `translate3d(0, ${y * 0.35}px, 0) scale(1.08)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", onScroll); };
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#3915AA]">
      <LocalBusinessSchema />
      {/* HERO */}
      <section className="relative overflow-hidden grain min-h-[92vh] flex items-center">
        <div className="absolute inset-0 will-change-transform" ref={heroBgRef} aria-hidden="true">
          <div className="absolute -top-40 right-[-10%] w-[900px] h-[900px] rounded-full bg-[#5B36E0] blur-[140px] opacity-70" />
          <div className="absolute bottom-[-30%] left-[-10%] w-[700px] h-[700px] rounded-full bg-[#FC5106]/25 blur-[140px]" />
          <div className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent)]" style={{ backgroundImage: "url(/brand/paw.png)", backgroundSize: "90px 90px" }} />
        </div>

        <div className="relative z-10 container-custom grid lg:grid-cols-12 gap-10 items-center pt-10 lg:pt-20 pb-12">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#FC5106] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FC5106]" />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/80">
                Now booking in Bhubaneswar
              </span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight text-white leading-[0.95]">
              Premium pet
              <br />
              <span className="text-gradient-primary">grooming</span> in
              <br />
              Bhubaneswar.
            </h1>

            <p className="mt-7 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed">
              Expert groomers, pet-safe products, and a stress-free studio your dog or cat will
              actually love. Studio visits or doorstep grooming — your call.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4 fade-up-delay-2">
              <Link to="/contact" data-testid="hero-book-cta" className="btn-primary">
                Book Grooming <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" data-testid="hero-services-cta" className="btn-secondary">
                Explore Services
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 fade-up-delay-3">
              <a
                href="https://www.justdial.com/Bhubaneshwar/Barknfurs-Infront-Of-Fire-Station-Sailashree-Vihar/0674PX674-X674-240607091724-Z7L8_BZDET/reviews"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-justdial-rating"
                className="flex items-center gap-2 group hover:opacity-90 transition-opacity"
              >
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#FC5106] fill-[#FC5106]" />
                  ))}
                </div>
                <span className="text-sm text-white/80">
                  <span className="font-semibold text-white">4.9</span> · 144 reviews on Justdial
                </span>
              </a>
              <div className="h-4 w-px bg-white/20 hidden sm:block" />
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Heart className="w-4 h-4 text-[#FC5106]" />
                500+ happy pets
              </div>
              <div className="h-4 w-px bg-white/20 hidden sm:block" />
              <div className="flex items-center gap-2 text-sm text-white/70">
                <ShieldCheck className="w-4 h-4 text-[#C9BCFF]" />
                Vet-approved products
              </div>
            </div>
          </div>

          {/* 3D paw */}
          <div className="lg:col-span-5 fade-up-delay-2 order-first lg:order-none">
            <div className="relative mx-auto max-w-[560px]">
              <div className="absolute inset-[12%] bg-[#FC5106]/35 rounded-full blur-[90px]" />
              <div className="absolute inset-[22%] translate-x-10 translate-y-10 bg-[#8B73FF]/40 rounded-full blur-[80px]" />
              <PawSurprise className="h-[300px] sm:h-[380px] lg:h-[540px]" />
              <div className="hidden sm:flex absolute bottom-6 left-0 glass-strong rounded-2xl p-3.5 items-center gap-3 animate-floatY" data-testid="hero-chip-doorstep">
                <div className="bg-[#FC5106] rounded-full p-2">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Doorstep Grooming</p>
                  <p className="text-white/60 text-xs">Available across Bhubaneswar</p>
                </div>
              </div>
              <div className="hidden sm:flex absolute top-8 right-0 glass-strong rounded-2xl px-4 py-3 items-center gap-2 animate-floatY [animation-delay:1.2s]" data-testid="hero-chip-rating">
                <Star className="w-4 h-4 text-[#FC5106] fill-[#FC5106]" />
                <p className="text-white font-semibold text-sm">4.9 rated</p>
              </div>
              {HERO_PETS.map((p) => (
                <div key={p.alt} className={`absolute ${p.pos} rounded-full p-1 bg-white/15 backdrop-blur-md border border-white/25 shadow-2xl animate-floatY pointer-events-none`} style={{ animationDelay: p.delay }}>
                  <img src={p.src} alt={p.alt} loading="lazy" className={`${p.size} rounded-full object-cover`} />
                </div>
              ))}
              <p className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[11px] uppercase tracking-[0.25em] text-white/50 whitespace-nowrap">Tap the paw</p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="border-y border-white/10 bg-[#2A0D8A] overflow-hidden py-6">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...TRUST_POINTS, ...TRUST_POINTS, ...TRUST_POINTS].map((p, i) => (
            <div key={i} className="flex items-center gap-3 mx-8 text-white/70">
              <Sparkles className="w-4 h-4 text-[#FC5106]" />
              <span className="font-heading text-base md:text-lg">{p}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <OffersSection source="home" />

      <section className="section-padding container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-3">What we offer</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
              Grooming services <span className="text-gradient-primary">crafted with care</span>
            </h2>
          </div>
          <Link to="/services" data-testid="home-view-all-services" className="btn-secondary !py-3 !px-6 !text-sm">
            View all services <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Big card */}
          <div className="md:col-span-7 group relative rounded-3xl overflow-hidden border border-white/10 bg-[#2E0F94] card-lift min-h-[420px]">
            <img
              src={featured[0].image}
              alt={featured[0].title}
              className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-65 transition-opacity"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1F0A66] via-[#1F0A66]/60 to-[#1F0A66]/20" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <div className="bg-[#FC5106] w-12 h-12 rounded-2xl flex items-center justify-center mb-5">
                <FirstIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                {featured[0].title}
              </h3>
              <p className="text-white/70 max-w-md mb-5">{featured[0].short}</p>
              <Link
                to="/services"
                data-testid="bento-service-bathing"
                className="inline-flex items-center gap-2 text-[#C9BCFF] hover:gap-3 transition-all text-sm font-medium"
              >
                Learn more <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Two stacked */}
          <div className="md:col-span-5 grid grid-cols-1 gap-5">
            {featured.slice(1, 3).map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  className="group relative rounded-3xl overflow-hidden border border-white/10 bg-[#2E0F94] card-lift min-h-[200px] flex"
                >
                  <div className="flex-1 p-6 md:p-7 flex flex-col justify-between">
                    <div>
                      <div className="bg-white/5 border border-white/10 w-11 h-11 rounded-xl flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-[#C9BCFF]" />
                      </div>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-1">
                        {s.title}
                      </h3>
                      <p className="text-white/60 text-sm leading-relaxed">{s.short}</p>
                    </div>
                    <span className="text-[#FC5106] text-xs font-semibold tracking-wider mt-3">
                      FROM ₹{s.fromPrice}
                    </span>
                  </div>
                  <div className="hidden sm:block w-32 lg:w-40 relative">
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Three small */}
          {featured.slice(3, 6).map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                className="md:col-span-4 group relative rounded-3xl overflow-hidden border border-white/10 bg-[#2E0F94] card-lift p-7"
                data-testid={`home-service-${s.id}`}
              >
                <div className="bg-[#FC5106]/10 border border-[#FC5106]/30 w-12 h-12 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#FC5106]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">{s.short}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[#C9BCFF] text-xs font-semibold tracking-wider">
                    FROM ₹{s.fromPrice}
                  </span>
                  <span className="text-xs text-white/40">{s.duration}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative section-padding container-custom section-light">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-8 -left-8 w-48 h-48 bg-[#FC5106]/15 blur-3xl rounded-full" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/15 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.pexels.com/photos/6131576/pexels-photo-6131576.jpeg?auto=compress&cs=tinysrgb&w=1100"
                alt="Groomer styling a dog"
                className="w-full h-[560px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex glass-strong rounded-2xl px-5 py-4 items-center gap-3">
              <div className="bg-[#3915AA] rounded-full p-2">
                <Award className="w-5 h-5 text-black" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Top Rated Studio</p>
                <p className="text-white/60 text-xs">in Bhubaneswar</p>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-3">Why pet parents choose us</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Loved by 500+ pets <br /> across Bhubaneswar.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
              We obsess over the details — from the temperature of the water to the scent of the
              shampoo. Every visit is calm, careful, and customised to your pet.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Certified expert groomers",
                "Vet-approved, pet-safe products",
                "Stress-free, calm studio",
                "Doorstep grooming available",
                "Affordable, transparent pricing",
                "Quick same-week appointments",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 group">
                  <CheckCircle2 className="w-5 h-5 text-[#FC5106] flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                  <span className="text-white/80">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/about" data-testid="home-learn-more" className="btn-secondary">
                Our story
              </Link>
              <Link to="/contact" data-testid="home-book-now-why" className="btn-primary">
                Book a visit <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="section-padding bg-[#300F9C]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-3">Glow up gallery</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
                Pets that leave smiling.
              </h2>
            </div>
            <Link to="/gallery" data-testid="home-view-gallery" className="btn-secondary !py-3 !px-6 !text-sm">
              View full gallery <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {GALLERY.slice(0, 4).map((g, i) => (
              <div key={i} className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={g.src}
                  alt={g.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F0A66]/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-semibold text-sm md:text-base">{g.title}</p>
                  <p className="text-white/60 text-xs">{g.pet}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-padding container-custom section-light">
        <div className="text-center mb-14">
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-3">Tail-wagging reviews</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
            What pet parents <span className="text-gradient-primary">say about us</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className="relative rounded-3xl glass p-7 card-lift"
              data-testid={`testimonial-${i}`}
            >
              <Quote className="w-8 h-8 text-[#FC5106]/40 mb-4" />
              <p className="text-white/80 leading-relaxed mb-6 text-sm">{t.text}</p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-white/50 text-xs">{t.pet}</p>
                </div>
              </div>
              <div className="flex gap-0.5 mt-3">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 text-[#FC5106] fill-[#FC5106]" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-custom pb-24">
        <div className="relative rounded-[36px] overflow-hidden border border-white/10 grain">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/4695939/pexels-photo-4695939.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#FC5106]/30 via-[#1F0A66]/70 to-white/20" />
          </div>
          <div className="relative grid md:grid-cols-2 gap-10 p-10 md:p-16 lg:p-20 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
                Ready to pamper your pet?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-md">
                Book a session at our Sailashree Vihar studio or get doorstep grooming anywhere in
                Bhubaneswar.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="cta-book-now" className="btn-primary">
                  Book now <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  data-testid="cta-call-now"
                  className="btn-secondary"
                >
                  <Phone className="w-4 h-4" /> Call us
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-5">
                <MapPin className="w-5 h-5 text-[#FC5106] mb-2" />
                <p className="text-white font-semibold text-sm mb-1">Studio Address</p>
                <p className="text-white/70 text-sm leading-relaxed">{BRAND.address}</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <Phone className="w-5 h-5 text-[#C9BCFF] mb-2" />
                <p className="text-white font-semibold text-sm mb-1">Call / WhatsApp</p>
                <p className="text-white/70 text-sm">{BRAND.phone}</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <HomeIcon className="w-5 h-5 text-[#FC5106] mb-2" />
                <p className="text-white font-semibold text-sm mb-1">Home Service</p>
                <p className="text-white/70 text-sm">Across Bhubaneswar</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <ShieldCheck className="w-5 h-5 text-[#C9BCFF] mb-2" />
                <p className="text-white font-semibold text-sm mb-1">Pet Safe</p>
                <p className="text-white/70 text-sm">Vet-approved products only</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST BLOG */}
      <LatestBlogSection />
    </div>
  );
}
